import { useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { t } from "i18next"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { formatDateString } from "@/lib/format-date"
import { getPersistedLanguage } from "@/lib/language-storage"
import { selectContentForLanguage } from "@/lib/select-content-for-language"
import { TeleconsultationDrawer } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import {
  dismissReasonOptions,
  PreventionProgramsDialog,
} from "@/modules/patients/detail/health-information/condition/prevention-programs-dialog"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientPrevention, PreventionProgramWithData } from "@/modules/patients/services/usePatientPrevention"
import { useDrawerStore } from "@/stores/useDrawerStore"

export const PREVENTION_ASSESSMENT_TELECONSULTATION_DRAWER = "PREVENTION_ASSESSMENT_TELECONSULTATION_DRAWER"

interface PreventionProgramItemProps {
  data: PatientPrevention
  isOpenConditionDrawer: boolean
  isAgendaPage?: boolean
  onSelectProgram: (program: PatientPrevention, isEditMode: boolean) => void
  onBookAndNavigate: (program: PatientPrevention) => void
}

const PreventionProgramItem = ({
  data,
  isOpenConditionDrawer,
  isAgendaPage,
  onSelectProgram,
  onBookAndNavigate,
}: PreventionProgramItemProps) => {
  const language = getPersistedLanguage()

  const getEligibilityMessage = (program: PatientPrevention): string => {
    const label = dismissReasonOptions.find((e) => e.value === program.dismissReason)?.label
    const programEgibilityText = t("patient.healthInformation.eligiblePreventionPrograms.programEligibility")
    const programName = selectContentForLanguage(language, program.name)
    const programInstruction = selectContentForLanguage(language, program.localizedInstruction)

    if (!program.status) {
      return `${programEgibilityText} ${programName}: ${programInstruction}`
    }

    if (program.status === "BOOKED") {
      return `${programEgibilityText} ${programName}: ${programInstruction}. (${t(
        "patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.already_book",
      )}, ${t("patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.updated_on")} ${program.lastUpdated && formatDateString(program.lastUpdated)})`
    }

    if (program.status === "DISMISSED") {
      return `${programEgibilityText} ${programName}: ${programInstruction}. (${
        label && t(label)
      }, ${t("patient.healthInformation.eligiblePreventionPrograms.rejectReasonContent.updated_on")} ${program.lastUpdated && formatDateString(program.lastUpdated)})`
    }

    return ""
  }

  return (
    <>
      {!data.status && (
        <Banner
          className="py-1"
          type={isAgendaPage ? "full-width" : "inline"}
          variant="warning"
          iconClassName={isAgendaPage ? "mt-2" : ""}>
          <div
            className={`flex w-full gap-2 ${isAgendaPage ? "flex-row justify-between" : "flex-col"}`}
            data-testid={data.id}>
            <span className={`text-sm ${isAgendaPage ? "mt-2.5" : ""}`} data-testid="prevention-banner">
              {getEligibilityMessage(data)}
            </span>
            <div className="flex flex-row justify-end">
              <Button
                variant={"tertiary"}
                onClick={() => {
                  onBookAndNavigate(data)
                }}
                data-testid="book-button">
                {t("patient.healthInformation.eligiblePreventionPrograms.book")}
              </Button>
              <Button
                variant={"tertiary"}
                onClick={() => {
                  onSelectProgram(data, false)
                }}
                data-testid="dont-show-again-button">
                {t("patient.healthInformation.eligiblePreventionPrograms.dontShowAgain")}
              </Button>
            </div>
          </div>
        </Banner>
      )}

      {isOpenConditionDrawer && (data.status === "BOOKED" || data.status === "DISMISSED") && (
        <Banner type="inline" variant="info">
          <div className="flex w-full flex-row items-center justify-between gap-2" data-testid={data.id}>
            <span className="text-sm" data-testid="prevention-info-banner">
              {getEligibilityMessage(data)}
            </span>
            <Button
              variant="tertiary"
              className="shrink-0"
              onClick={() => {
                onSelectProgram(data, true)
              }}
              data-testid="edit-prevention-button">
              {t("patient.healthInformation.eligiblePreventionPrograms.edit")}
            </Button>
          </div>
        </Banner>
      )}
    </>
  )
}

const PreventionProgramsContent = ({
  isOpenConditionDrawer,
  isAgendaPage,
  patientPreventionItems,
  closeDrawer,
}: {
  isOpenConditionDrawer: boolean
  isAgendaPage?: boolean
  patientPreventionItems?: PreventionProgramWithData[]
  closeDrawer?: () => void
}) => {
  const navigate = useNavigate()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const location = useLocation()
  const patientId = usePatientParam()
  const country = useCountryParam()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState<PatientPrevention | undefined>(undefined)
  const [isEditPreventionProgramsMode, setIsEditMode] = useState(false)

  const bookAndNavigate = (data: PatientPrevention) => {
    if (!isAgendaPage) navigate(`${location.pathname}/agenda${location.search}`)
    openDrawer(
      () => (
        <TeleconsultationDrawer
          mode={{
            kind: "ADD_APPOINTMENT",
            data: {
              defaultService: "PREVENTION_ASSESSMENT",
              defaultPreventionProgramKey: data.key,
            },
          }}
          countryCode={country}
          patientId={patientId}
          appointmentId={null}
        />
      ),
      PREVENTION_ASSESSMENT_TELECONSULTATION_DRAWER,
    )
  }

  const handleSelectProgram = (program: PatientPrevention, isEditMode: boolean) => {
    setSelectedProgram(program)
    setIsEditMode(isEditMode)
    setIsDialogOpen(true)
  }

  return (
    <>
      {patientPreventionItems?.map((item) => (
        <PreventionProgramItem
          key={item.id}
          data={item.data}
          isOpenConditionDrawer={isOpenConditionDrawer}
          isAgendaPage={isAgendaPage}
          onSelectProgram={handleSelectProgram}
          onBookAndNavigate={bookAndNavigate}
        />
      ))}

      {/* Render the dialog outside the map */}
      {isDialogOpen && selectedProgram && (
        <PreventionProgramsDialog
          preventionProgramData={selectedProgram}
          patientId={patientId}
          country={country}
          isEditPreventionProgramsMode={isEditPreventionProgramsMode}
          currentDismissReason={isEditPreventionProgramsMode ? (selectedProgram.dismissReason ?? undefined) : undefined}
          onClose={() => {
            setIsDialogOpen(false)
            setIsEditMode(false)
            setSelectedProgram(undefined)
            closeDrawer?.()
          }}
        />
      )}
    </>
  )
}

export { PreventionProgramsContent }
